import { reaction } from "mobx";
import mixpanel from "mixpanel-browser";
import * as amplitude from "@amplitude/analytics-browser";

import { firebaseAnalytics } from "../firebase";
import packageJSON from "../../../package.json";

export default class Analytics {
  constructor(makeMobxStore, _) {
    this._ = _;
    this.reset = makeMobxStore(this);

    // track number of mins user is actively interacting with app
    if (typeof window !== "undefined") {
      const isProduction =
        process.env.NODE_ENV === "production" &&
        window.TEST === undefined &&
        window.location.hostname === "bytez.com";

      amplitude.init(
        isProduction
          ? "86dd73bdeb324642099cff55fb70d4be"
          : "4c96a794f7b5d393d337cc4600f99ed8",
        undefined,
        {
          defaultTracking: true,
          appVersion: packageJSON.version,
          transport: "beacon",
          useBatch: false
        }
      );
      mixpanel.init(
        isProduction
          ? "677d30d236babbeb44c189b84424fd67"
          : "4367a57e7b30b980eee72802d1f825d1",
        {
          api_transport: "sendBeacon",
          batch_requests: false,
          persistence: "localStorage",
          ignore_dnt: true
          // debug: process.env.NODE_ENV === "development"
        }
      );
      // log when not in iframe
      if (window === window.parent) {
        console.log("App v", packageJSON.version);
      }

      reaction(
        () => this._.user.uid,
        () => this.track.engagement()
      );
      // track user at session start
      reaction(
        () => ({
          isAnonymous: this._.user.session.isAnonymous
          // org's going to come up as undefined or as populated, in either case we need the reaction to run when its state has changed
          // we must keep this here to ensure user.profile() is called when it does update
          // orgLoading: this._.org.loading
        }),

        ({ isAnonymous }) => {
          if (isAnonymous === false) {
            this.track.user.id();
            this.track.user.profile();
          }
        }
      );
    }
  }
  set = {
    context: (container, action, props = {}) => {
      let context = {};

      if (container && action) {
        const eventName = this.capitalize(`${container} ${action}`);
        // track the initial event
        this.track.event(eventName, props);
        // set the event name to container, so it can be passed to children
        context = { ...props, container: eventName };
      }

      this.context = context;
    }
  };
  track = {
    event:
      typeof window === "undefined"
        ? () => {}
        : (eventName, properties) => {
            const props = {
              ...this.enrichedContext,
              ...properties,
              time: new Date()
            };

            eventName = this.capitalize(eventName);

            firebaseAnalytics.track(eventName, props);
            mixpanel.track(eventName, props);
            amplitude.track(eventName, props);

            console.debug("event", { name: eventName, props });
          },
    engagement: () => {
      const eventsToMonitor = ["click", "keypress", "wheel", "touchstart"];
      let engaged = true;
      // if user engages, record, and remove listeners
      function checkIfUserEngaged() {
        if (engaged === false && document.hidden === false) {
          engaged = true;
          eventsToMonitor.forEach(event =>
            window.removeEventListener(event, checkIfUserEngaged)
          );
        }
      }
      clearInterval(this.engagementTimer);
      // record engagment 1 min
      this.engagementTimer = setInterval(() => {
        if (engaged) {
          this.track.event("User Engaged");
          // reset
          engaged = false;
          // lets monitor these events, to see if user is using app
          eventsToMonitor.forEach(event =>
            window.addEventListener(event, checkIfUserEngaged, {
              passive: true
            })
          );
        }
      }, 60e3);
    },
    user: {
      id: () => {
        if (this._.user.session.isAnonymous === false) {
          mixpanel.identify(this._.user.uid);
          amplitude.setUserId(this._.user.uid);
        }
      },
      profile: () => {
        if (this._.user.session.isAnonymous === false) {
          const { user } = this._;
          const analyticsProfile = {
            $avatar: user.session.photoURL,
            $distinct_id: user.uid,
            $email: user.session.email,
            $name: user.session.displayName,
            ...user.profile
          };

          // if (org.exists) {
          //   analyticsProfile.orgId = org.id;
          //   analyticsProfile.orgName = org.name;
          // }

          firebaseAnalytics.setUserProfile(analyticsProfile);
          mixpanel.people.set(analyticsProfile);

          const profile = new amplitude.Identify();

          for (const key in analyticsProfile) {
            profile.set(key, analyticsProfile[key]);
          }

          amplitude.identify(profile);
        }
      }
    }
  };
  capitalize = eventName =>
    eventName
      .split(" ")
      .map(subname => `${subname[0].toUpperCase()}${subname.slice(1)}`)
      .join(" ");
  reportWebVital = metric => {
    const { id, name } = metric;
    const value = Math.round(
      name === "CLS" ? metric.value * 1000 : metric.value
    );
    // console.log(metric);
    // google analytics
    this.track.event(name, { id, value });
  };
  signOut = () => {
    this.track.event("User Logout");
    mixpanel.reset();
    amplitude.reset();
  };
  get enrichedContext() {
    return {
      // ...properties,
      context: this.context,
      // mixpanel specific attributes
      // user
      userId: this._.user.uid,
      userName: this._.user.session.displayName,
      //
      // userAccountType: accountType, TODO: what is this?
      //
      // org
      // orgId: this._.org.id,
      // orgName: this._.org.name,
      // surface specific attributes
      surface: "pwa",
      appVersion: packageJSON.version,
      // duplicate info for mixpanel, but useful to us for BE call
      pathname:
        typeof window === "undefined" ? undefined : window?.location.pathname,
      screenSize: this._.device.size
    };
  }
}
